import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../shared/services/auth.service';
import { CapacitorHttp, HttpOptions } from '@capacitor/core';
import { jwtDecode } from "jwt-decode";
import { Subscription } from 'rxjs';
import { AppConfig } from '../app.config';
import { LoginTrackingService, LOGIN_STAGE } from '../shared/services/login-tracking.service';

export interface StateData {
  id: string;
  platform: 'web' | 'android' | 'ios';
  [code_challange: string]: any;
  access_denied: boolean;
  session_id: string;
  device_id: string;
}

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})

export class AuthComponent {
  private callbackProcessed: boolean = false;
  public pageMessage: string = "Redirecting...";
  private readonly networkSubscription!: Subscription;
  private broadcastRetryTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,    
    private readonly loginTracking: LoginTrackingService,
  ) {

    this.networkSubscription = this.authService.isOnline$.subscribe((status) => {
      // Do nothing while offline.
      if (!status) {
        return;
      }

      // Prevent the same B2C callback from being processed again.
      if (this.callbackProcessed) {
        console.log(
          'AuthComponent: B2C callback already processed. Ignoring.'
        );
        return;
      }

      this.processAuthCallback();
    });    
  }

  ngOnDestroy(): void {
    this.networkSubscription?.unsubscribe();

    if (this.broadcastRetryTimeout) {
      clearTimeout(this.broadcastRetryTimeout);
      this.broadcastRetryTimeout = undefined;
    }
  }

  private processAuthCallback(): void {
    this.callbackProcessed = true;
    const state: string = this.route.snapshot.queryParamMap.get('state') ?? '';
    const code: string = this.route.snapshot.queryParamMap.get('code') ?? '';
    const logout: string = this.route.snapshot.queryParamMap.get('logout') ?? '';
    const error: string = this.route.snapshot.queryParamMap.get('error') ?? '';
    const errorDescription = this.route.snapshot.queryParamMap.get('error_description') ?? '';

    if (state) {
      let state_data: StateData;
      try {
        state_data = jwtDecode<StateData>(state);
      } catch (err) {
        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_FAILED,
          `Unable to decode B2C state. stateLength: ${state.length}`,
          ''
        );

        console.error('Unable to decode B2C state:', err);
        this.networkSubscription?.unsubscribe();
        this.authService.showLoginErrorModal();
        return;
      }

      const platform = state_data?.platform;
      const session_id = state_data?.session_id;
      const device_id = state_data?.device_id;

      // Root-level idempotency: same OAuth state must not be processed twice across remounts.
      if (!this.authService.claimOAuthCallback(state)) {
        this.networkSubscription?.unsubscribe();
        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_DUPLICATE_CALLBACK,
          `Duplicate OAuth callback ignored. stateLength: ${state.length}`,
          session_id,
          device_id,
          platform
        );
        console.log('AuthComponent: OAuth state already claimed. Ignoring duplicate callback.');
        return;
      }

      // Remove OAuth query params so a remounted AuthComponent cannot re-read the same code/state.
      this.clearOAuthQueryParams();

      console.log('AuthComponent authMiddlewareHandler invoked');
      this.loginTracking.track(
        LOGIN_STAGE.AUTH_MIDDLEWARE_INVOKED,
        `B2C callback received. hasState:${!!state}, stateLength: ${state.length}, platform: ${state_data.platform}, hasSessionId: ${!!state_data.session_id}, hasDeviceId: ${!!state_data.device_id}, hasCode: ${!!code}, codeLength: ${code.length}, hasError: ${!!error}`,
        session_id,
        device_id,
        platform
      );

      if (code || logout) {
        // this.authService.isLoginInProgress = true;
        this.networkSubscription?.unsubscribe();
        this.authMiddlewareHandler(state, code, state_data);
      } else if (error) {
        this.networkSubscription?.unsubscribe();

        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_FAILED,
          `B2C returned OAuth error. error=${error}, hasErrorDescription=${!!errorDescription}`,
          session_id,
          device_id,
          platform
        );
        
        this.authService.showLoginErrorModal(state);
      } else {
        this.networkSubscription?.unsubscribe();
        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_FAILED, 
          `Invalid request.`, 
          session_id, 
          device_id,
          platform
        );
      }
    } else {
      this.networkSubscription?.unsubscribe();  
    }
  }

  private clearOAuthQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  authMiddlewareHandler(
    state: string,
    code: string,
    state_data: StateData,
    retry: number = 0
  ) {
    const platform = state_data.platform;
    const session_id = state_data?.session_id;
    const device_id = state_data?.device_id;

    if (code) {
      this.pageMessage = "Authorizing...";

      if (platform !== 'web') {
        const options: HttpOptions = {
          url: `${AppConfig.settings?.SIGNALR_WRAPPER_API_BASE_URL}/broadcast`,
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            state,
            code,
            userId: state_data.id
          },
        };

        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_INVOKED,
          `SignalR broadcast attempt ${retry + 1}.`,
          session_id,
          device_id,
          platform
        );

        CapacitorHttp.post(options)
          .then((response) => {
            if (response.status === 200) {
              this.loginTracking.track(
                LOGIN_STAGE.AUTH_MIDDLEWARE_SUCCESS,
                `SignalR broadcast completed. HTTP status: ${response.status}, attempt: ${retry + 1}.`,
                session_id,
                device_id,
                platform
              );
              return;
            }

            this.handleBroadcastFailure(
              state,
              code,
              state_data,
              retry,
              `HTTP status: ${response?.status ?? 'unknown'}`
            );
          })
          .catch((error) => {
            this.handleBroadcastFailure(
              state,
              code,
              state_data,
              retry,
              `errorType: ${error?.name ?? 'unknown'}`
            );
          });

      } else {
        this.loginTracking.track(
          LOGIN_STAGE.AUTH_MIDDLEWARE_SUCCESS,
          `Invoked acquireB2CAccessToken.`,
          session_id,
          device_id,
          platform
        );

        this.authService.acquireB2CAccessToken(code, session_id);
      }
    } else {
      console.log('AuthComponent authMiddlewareHandler for logout');

      this.loginTracking.track(
        LOGIN_STAGE.AUTH_MIDDLEWARE_SUCCESS,
        `Invoked for logout.`,
        session_id,
        device_id,
        platform
      );

      if (state_data['access_denied']) {
        window.location.href = "access_denied";
      } else {
        window.location.href =
          this.authService.getB2CLoginUrl(
            state,
            state_data['code_challange']
          );
      }
    }
  }

  private handleBroadcastFailure(
    state: string,
    code: string,
    state_data: StateData,
    retry: number,
    reason: string
  ): void {

    const platform = state_data.platform;
    const session_id = state_data?.session_id;
    const device_id = state_data?.device_id;

    const maxRetries = 2;

    if (retry < maxRetries) {
      this.loginTracking.track(
        LOGIN_STAGE.AUTH_MIDDLEWARE_FAILED,
        `SignalR broadcast failed. ${reason}. Scheduling attempt ${retry + 2} of ${maxRetries + 1} in 3 seconds.`,
        session_id,
        device_id,
        platform
      );

      this.broadcastRetryTimeout = setTimeout(() => {

        this.broadcastRetryTimeout = undefined;

        this.authMiddlewareHandler(
          state,
          code,
          state_data,
          retry + 1
        );

      }, 3000);

      return;
    }

    this.loginTracking.track(
      LOGIN_STAGE.AUTH_MIDDLEWARE_FAILED,
      `SignalR broadcast failed after ${maxRetries + 1} attempts. ${reason}`,
      session_id,
      device_id,
      platform
    );

    this.authService.showLoginErrorModal(state);
  }
}